// ============================================================================
// California Form DE-4: Employee's Withholding Allowance Certificate
// State Tax Withholding Form
// ============================================================================

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  FileText,
  Check,
  AlertTriangle,
  Calculator,
  DollarSign,
  User,
  MapPin,
  HelpCircle,
  Info,
  ChevronRight,
  Save,
  Send,
  CheckCircle2,
  Building2,
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import ESignature, { SignatureDisplay } from '../compliance/ESignature';
import { CA_DE4_FILING_STATUS, CA_DE4_FORM } from '@/lib/californiaForms';

// ============================================================================
// FORM FIELD COMPONENT
// ============================================================================

function FormField({ label, required, error, children, helpText, tooltip }) {
  return (
    <div className="space-y-1">
      <Label className="flex items-center gap-1">
        {label}
        {required && <span className="text-red-500">*</span>}
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-3 w-3 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </Label>
      {helpText && <p className="text-xs text-gray-500">{helpText}</p>}
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

// ============================================================================
// US STATES LIST
// ============================================================================

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'District of Columbia' },
];

// ============================================================================
// MAIN FORM DE-4 COMPONENT
// ============================================================================

export default function FormDE4({
  employee,
  existingSubmission,
  onSave,
  onSubmit,
  disabled = false,
  className = '',
}) {
  // Get profile data for pre-population
  const getProfileData = () => {
    const profile = employee?.profile || {};
    const address = profile.address?.mailing || {};
    return {
      firstName: profile.firstName || employee?.firstName || '',
      lastName: profile.lastName || employee?.lastName || '',
      street: address.address1 || '',
      apt: address.address2 || '',
      city: address.city || '',
      state: address.state || 'CA',
      zip: address.zip || '',
    };
  };

  const profileData = getProfileData();

  const [formData, setFormData] = useState(existingSubmission || {
    firstName: profileData.firstName,
    middleInitial: '',
    lastName: profileData.lastName,
    ssn: '',
    address: {
      street: profileData.street,
      apt: profileData.apt,
      city: profileData.city,
      state: profileData.state,
      zip: profileData.zip,
    },
    filingStatus: '',
    regularAllowances: 0,
    additionalAllowances: 0,
    additionalWithholding: 0,
    exempt: false,
    exemptReason: '',
  });
  const [errors, setErrors] = useState({});
  const [showSignature, setShowSignature] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(existingSubmission?.status === 'complete');

  const totalAllowances = (parseInt(formData.regularAllowances) || 0) + (parseInt(formData.additionalAllowances) || 0);

  // Handle field changes
  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleAddressChange = (field, value) => {
    setFormData({
      ...formData,
      address: { ...formData.address, [field]: value },
    });
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.ssn) newErrors.ssn = 'Social Security Number is required';
    if (!formData.address?.street) newErrors.address = { ...newErrors.address, street: 'Street address is required' };
    if (!formData.address?.city) newErrors.address = { ...newErrors.address, city: 'City is required' };
    if (!formData.address?.zip) newErrors.address = { ...newErrors.address, zip: 'ZIP code is required' };
    if (!formData.filingStatus && !formData.exempt) newErrors.filingStatus = 'Filing status is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave?.({
        ...formData,
        status: 'draft',
        updatedAt: new Date().toISOString(),
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle submit
  const handleSubmit = () => {
    if (!validateForm()) return;
    setShowSignature(true);
  };

  // Handle signature
  const handleSigned = async (signatureData) => {
    setIsSubmitting(true);
    try {
      await onSubmit?.({
        ...formData,
        signature: signatureData,
        status: 'complete',
        submittedAt: new Date().toISOString(),
      });
      setIsComplete(true);
      setShowSignature(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="h-6 w-6 text-amber-600" />
            Form DE 4: Employee's Withholding Allowance Certificate
          </h2>
          <p className="text-gray-600 mt-1">
            State of California • Employment Development Department • {CA_DE4_FORM.version}
          </p>
        </div>
        {isComplete && (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Completed
          </Badge>
        )}
      </div>

      {/* Instructions */}
      <Alert className="bg-amber-50 border-amber-200">
        <Info className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-800">California State Withholding</AlertTitle>
        <AlertDescription className="text-amber-700">
          Complete this form so your employer can withhold the correct California state income tax
          from your pay. If you fail to file this form, your employer must withhold California
          income tax as if you are single with zero withholding allowances.
        </AlertDescription>
      </Alert>

      {!isComplete && (
        <>
          {/* Section 1: Personal Information */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <User className="h-4 w-4 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-base">Section 1: Personal Information</CardTitle>
                  <CardDescription>Enter your personal identification information</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <FormField label="First Name" required error={errors.firstName}>
                  <Input
                    value={formData.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    placeholder="First name"
                    disabled={disabled}
                  />
                </FormField>
                <FormField label="Middle Initial">
                  <Input
                    value={formData.middleInitial}
                    onChange={(e) => handleChange('middleInitial', e.target.value.slice(0, 1))}
                    placeholder="M.I."
                    maxLength={1}
                    disabled={disabled}
                  />
                </FormField>
                <FormField label="Last Name" required error={errors.lastName}>
                  <Input
                    value={formData.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    placeholder="Last name"
                    disabled={disabled}
                  />
                </FormField>
                <FormField
                  label="Social Security Number"
                  required
                  error={errors.ssn}
                  tooltip="Your SSN is required for state tax withholding"
                >
                  <Input
                    value={formData.ssn || ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                      const formatted = value.replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3');
                      handleChange('ssn', formatted);
                    }}
                    placeholder="XXX-XX-XXXX"
                    disabled={disabled}
                  />
                </FormField>
              </div>
            </CardContent>
          </Card>

          {/* Section 2: Address */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-base">Section 2: Address</CardTitle>
                  <CardDescription>Enter your current home address</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-3">
                  <FormField label="Street Address" required error={errors.address?.street}>
                    <Input
                      value={formData.address?.street || ''}
                      onChange={(e) => handleAddressChange('street', e.target.value)}
                      placeholder="Street address"
                      disabled={disabled}
                    />
                  </FormField>
                </div>
                <FormField label="Apt/Suite/Unit">
                  <Input
                    value={formData.address?.apt || ''}
                    onChange={(e) => handleAddressChange('apt', e.target.value)}
                    placeholder="Apt #"
                    disabled={disabled}
                  />
                </FormField>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField label="City" required error={errors.address?.city}>
                  <Input
                    value={formData.address?.city || ''}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    placeholder="City"
                    disabled={disabled}
                  />
                </FormField>
                <FormField label="State" required>
                  <Select
                    value={formData.address?.state || 'CA'}
                    onValueChange={(value) => handleAddressChange('state', value)}
                    disabled={disabled}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="ZIP Code" required error={errors.address?.zip}>
                  <Input
                    value={formData.address?.zip || ''}
                    onChange={(e) => handleAddressChange('zip', e.target.value)}
                    placeholder="ZIP Code"
                    maxLength={10}
                    disabled={disabled}
                  />
                </FormField>
              </div>
            </CardContent>
          </Card>

          {/* Section 3: Filing Status */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-base">Section 3: Filing Status</CardTitle>
                  <CardDescription>Choose your filing status for California withholding</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  Your filing status determines the withholding rate applied to your wages.
                  Choose the status that best describes your situation.
                </AlertDescription>
              </Alert>

              <RadioGroup
                value={formData.filingStatus}
                onValueChange={(value) => handleChange('filingStatus', value)}
                disabled={disabled || formData.exempt}
                className="space-y-3"
              >
                {CA_DE4_FILING_STATUS.map((status) => (
                  <div
                    key={status.value}
                    className={`flex items-start space-x-3 p-4 rounded-lg border transition-colors ${formData.filingStatus === status.value
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 hover:border-gray-300'
                      }`}
                  >
                    <RadioGroupItem value={status.value} id={`de4-${status.value}`} />
                    <Label htmlFor={`de4-${status.value}`} className="flex-1 cursor-pointer">
                      <span className="font-medium flex items-center gap-2">
                        Filing Status {status.code}: {status.label}
                      </span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              {errors.filingStatus && (
                <p className="text-xs text-red-500">{errors.filingStatus}</p>
              )}
            </CardContent>
          </Card>

          {/* Section 4: Withholding Allowances */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <Calculator className="h-4 w-4 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-base">Section 4: Withholding Allowances</CardTitle>
                  <CardDescription>Calculate your total withholding allowances</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Regular Allowances */}
              <div className="p-4 bg-gray-50 rounded-lg border space-y-4">
                <h4 className="font-medium text-gray-800">Regular Withholding Allowances</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span>A. Enter "1" for yourself</span>
                    <span className="font-medium">1</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>B. Enter "1" if married/RDP and not claiming exemption for spouse</span>
                    <span className="font-medium">1</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>C. Enter number of dependents</span>
                    <span className="font-medium">(variable)</span>
                  </div>
                </div>
                <FormField label="Total Regular Allowances (Line 1)" tooltip="Sum of allowances from the worksheet above">
                  <Input
                    type="number"
                    min="0"
                    value={formData.regularAllowances || ''}
                    onChange={(e) => handleChange('regularAllowances', parseInt(e.target.value) || 0)}
                    placeholder="0"
                    disabled={disabled || formData.exempt}
                    className="w-32"
                  />
                </FormField>
              </div>

              {/* Additional Allowances */}
              <div className="p-4 bg-gray-50 rounded-lg border space-y-4">
                <h4 className="font-medium text-gray-800">Additional Withholding Allowances (Line 2)</h4>
                <p className="text-sm text-gray-600">
                  Use the worksheet on page 2 of Form DE 4 if you expect to itemize deductions
                  or have other income adjustments.
                </p>

                <Accordion type="single" collapsible>
                  <AccordionItem value="worksheet">
                    <AccordionTrigger className="text-sm">
                      <Calculator className="h-4 w-4 mr-2" />
                      Additional Withholding Allowance Worksheet
                    </AccordionTrigger>
                    <AccordionContent className="space-y-4 pt-4">
                      <Alert className="bg-gray-100">
                        <Info className="h-4 w-4" />
                        <AlertDescription>
                          Complete this worksheet if you expect to have itemized deductions
                          significantly above the standard deduction.
                        </AlertDescription>
                      </Alert>

                      <div className="space-y-3">
                        <FormField label="A. Estimated itemized deductions for the year">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">$</span>
                            <Input
                              type="number"
                              placeholder="0"
                              disabled={disabled}
                              className="w-32"
                            />
                          </div>
                        </FormField>

                        <div className="p-3 bg-amber-50 rounded">
                          <p className="text-sm">
                            <strong>B. Standard Deduction:</strong><br />
                            Single/Married filing separately: $5,363<br />
                            Married/RDP filing jointly or Head of Household: $10,726
                          </p>
                        </div>

                        <FormField label="C. Subtract B from A (if less than zero, enter 0)">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">$</span>
                            <Input
                              type="number"
                              placeholder="0"
                              disabled={disabled}
                              className="w-32"
                            />
                          </div>
                        </FormField>

                        <FormField label="D. Divide line C by $1,000 (round to nearest whole number)">
                          <Input
                            type="number"
                            placeholder="0"
                            disabled={disabled}
                            className="w-32"
                          />
                        </FormField>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <FormField label="Additional Allowances (Line 2)">
                  <Input
                    type="number"
                    min="0"
                    value={formData.additionalAllowances || ''}
                    onChange={(e) => handleChange('additionalAllowances', parseInt(e.target.value) || 0)}
                    placeholder="0"
                    disabled={disabled || formData.exempt}
                    className="w-32"
                  />
                </FormField>
              </div>

              {/* Total */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-amber-800">
                    Total Withholding Allowances (Line 1 + Line 2)
                  </span>
                  <div className="text-xl font-bold text-amber-600">
                    {totalAllowances}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 5: Additional Withholding */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <DollarSign className="h-4 w-4 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-base">Section 5: Additional Withholding (Optional)</CardTitle>
                  <CardDescription>Request extra withholding if desired</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <FormField
                label="Additional Amount to Withhold Each Pay Period"
                helpText="Enter any additional amount you want withheld from each paycheck"
                tooltip="Use this to increase your withholding if you want to avoid owing taxes"
              >
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.additionalWithholding || ''}
                    onChange={(e) => handleChange('additionalWithholding', parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    disabled={disabled || formData.exempt}
                    className="w-32"
                  />
                </div>
              </FormField>
            </CardContent>
          </Card>

          {/* Section 6: Exemption */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-gray-600" />
                </div>
                <div>
                  <CardTitle className="text-base">Section 6: Exemption from Withholding</CardTitle>
                  <CardDescription>Only claim if you qualify for exemption</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-yellow-50 border-yellow-200">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <AlertTitle className="text-yellow-800">Important</AlertTitle>
                <AlertDescription className="text-yellow-700">
                  You may claim exemption from withholding only if both of the following apply:
                  <ul className="list-disc list-inside mt-2">
                    <li>Last year you had a right to a refund of ALL California income tax withheld because you had no tax liability</li>
                    <li>This year you expect a refund of ALL California income tax withheld because you expect no tax liability</li>
                  </ul>
                  <p className="mt-2 font-medium">
                    Exemption expires February 15 of the following year.
                  </p>
                </AlertDescription>
              </Alert>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="exempt"
                  checked={formData.exempt || false}
                  onCheckedChange={(checked) => {
                    handleChange('exempt', checked);
                    if (checked) {
                      handleChange('filingStatus', '');
                      handleChange('regularAllowances', 0);
                      handleChange('additionalAllowances', 0);
                      handleChange('additionalWithholding', 0);
                    }
                  }}
                  disabled={disabled}
                />
                <Label htmlFor="exempt" className="font-medium cursor-pointer">
                  I claim exemption from withholding for {new Date().getFullYear()}
                </Label>
              </div>

              {formData.exempt && (
                <FormField label="Reason for Exemption">
                  <Input
                    value={formData.exemptReason || ''}
                    onChange={(e) => handleChange('exemptReason', e.target.value)}
                    placeholder="Explain why you qualify for exemption"
                    disabled={disabled}
                  />
                </FormField>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={handleSave} disabled={isSaving}>
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button onClick={handleSubmit} className="bg-amber-600 hover:bg-amber-700">
              <Send className="h-4 w-4 mr-2" />
              Sign & Submit
            </Button>
          </div>
        </>
      )}

      {/* Completed State */}
      {isComplete && existingSubmission?.signature && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-green-800 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              Form DE 4 Submitted
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Filing Status:</span>
                <p className="font-medium">
                  {CA_DE4_FILING_STATUS.find(s => s.value === formData.filingStatus)?.label ||
                    (formData.exempt ? 'Exempt' : 'Not specified')}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Total Allowances:</span>
                <p className="font-medium">{formData.exempt ? 'N/A (Exempt)' : totalAllowances}</p>
              </div>
              <div>
                <span className="text-gray-500">Additional Withholding:</span>
                <p className="font-medium">${formData.additionalWithholding || 0}</p>
              </div>
              <div>
                <span className="text-gray-500">Status:</span>
                <p className="font-medium">{formData.exempt ? 'Exempt' : 'Active'}</p>
              </div>
            </div>
            <Separator />
            <SignatureDisplay signature={existingSubmission.signature} />
            <p className="text-sm text-green-700">
              Submitted on {new Date(existingSubmission.submittedAt).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Signature Dialog */}
      <Dialog open={showSignature} onOpenChange={setShowSignature}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <ESignature
            documentTitle="Form DE 4: Employee's Withholding Allowance Certificate"
            documentType="form_de4"
            documentId={existingSubmission?.id || 'new'}
            signerId={employee?.id}
            signerName={`${formData.firstName} ${formData.lastName}`}
            signerEmail={employee?.email || ''}
            onSign={handleSigned}
            onCancel={() => setShowSignature(false)}
            disabled={isSubmitting}
            consentText={`I certify under penalty of perjury that I am entitled to the number of withholding allowances claimed on this certificate, or if claiming exemption from withholding, that I am entitled to claim the exempt status.

I understand that if I make a false statement or claim on this certificate, I may be subject to penalties under California law.`}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// DE-4 STATUS CARD (For Dashboard)
// ============================================================================

export function DE4StatusCard({ submission, employee, onView, onEdit }) {
  const isComplete = submission?.status === 'complete';

  return (
    <Card className={`transition-all hover:shadow-md ${isComplete ? 'border-green-200' : ''}`}>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isComplete ? 'bg-green-100' : 'bg-amber-100'
              }`}>
              <FileText className={`h-5 w-5 ${isComplete ? 'text-green-600' : 'text-amber-600'}`} />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Form DE 4</h4>
              <p className="text-sm text-gray-500">California State Withholding</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isComplete ? (
              <Badge className="bg-green-100 text-green-800 border-green-200">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Complete
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                Pending
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={() => isComplete ? onView?.(submission) : onEdit?.(submission)}>
              {isComplete ? 'View' : 'Complete'}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>

        {isComplete && submission?.submittedAt && (
          <p className="text-xs text-gray-500 mt-3">
            Last updated: {new Date(submission.submittedAt).toLocaleDateString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
